/**
 * A QUICK ROW OF EMOJI UNDER A REPLY BOX - design 24, T21 (the owner's option 1, 2026-10-11).
 *
 * Readers write to their authors in hearts, stars and the moon, so the reply boxes on Comments and
 * Messages offer the favorites one click away: ten buttons under the text, hearts, stars and the
 * moon first, each with a spoken name. A click puts the emoji where the cursor is (in place of a
 * selection) and leaves the cursor just after it, in the text. No library and no full picker: every
 * other emoji is on the device's own keyboard, and the quiet line after the row says so. The server
 * takes any emoji already; this only saves the author a trip to the keyboard's emoji key.
 *
 * WHY THE CURSOR IS PUT BACK AFTER THE RENDER. The text field is controlled: React writes the new
 * value into it, and a written value leaves the cursor at the end. So the place is kept until the
 * field shows that value, then set (`useLayoutEffect`). A pointer press does not take the focus from
 * the text at all; a key press on a button gives it back with the cursor.
 *
 * ONE EMOJI IS ONE CHARACTER TO A READER, though it is two or more in the string (❤️ is two code
 * points). `countGraphemes` is the count a person makes, as the application form counts
 * (features/application/domain/limits.ts, `graphemeLength`); a reply counter shows it.
 */
import { ActionIcon, Group, Stack, Text, Textarea } from '@mantine/core';
import type { TextareaProps } from '@mantine/core';
import { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from '../../../i18n';
import type { ENGLISH_RESOURCES } from '../../../i18n';

export interface QuickEmojiChoice {
  readonly emoji: string;
  /** What a screen reader says for the button: `cabinet:emoji.names.*`. */
  readonly name: keyof (typeof ENGLISH_RESOURCES)['cabinet']['emoji']['names'];
}

/** The row, in the order drawn: hearts, stars and the moon first, as readers write them. */
export const QUICK_EMOJI: readonly QuickEmojiChoice[] = [
  { emoji: '❤️', name: 'heart' },
  { emoji: '🌙', name: 'moon' },
  { emoji: '✨', name: 'sparkles' },
  { emoji: '⭐', name: 'star' },
  { emoji: '🙏', name: 'foldedHands' },
  { emoji: '🌕', name: 'fullMoon' },
  { emoji: '💫', name: 'dizzyStar' },
  { emoji: '🥰', name: 'smileWithHearts' },
  { emoji: '😊', name: 'smile' },
  { emoji: '🔮', name: 'crystalBall' },
];

export interface QuickEmojiProps {
  /** The text field the emoji go into. */
  readonly field: RefObject<HTMLTextAreaElement | null>;
  /** The field's text, as its owner holds it. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean | undefined;
}

export function QuickEmoji({ field, value, onChange, disabled = false }: QuickEmojiProps) {
  const { t } = useTranslation('cabinet');
  const caret = useRef<{ readonly value: string; readonly at: number } | null>(null);

  useLayoutEffect(() => {
    const pending = caret.current;
    if (pending === null) {
      return;
    }
    caret.current = null;
    const element = field.current;
    // The owner may have kept its old text; then there is nothing to put the cursor in.
    if (element === null || pending.value !== value) {
      return;
    }
    element.focus();
    element.setSelectionRange(pending.at, pending.at);
  }, [field, value]);

  const insert = (emoji: string): void => {
    const element = field.current;
    const start = element?.selectionStart ?? value.length;
    const end = element?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + emoji + value.slice(end);
    caret.current = { value: next, at: start + emoji.length };
    onChange(next);
  };

  return (
    <Group gap="xs" wrap="wrap">
      <Group gap={2} wrap="wrap" role="group" aria-label={t('emoji.group')}>
        {QUICK_EMOJI.map(({ emoji, name }) => (
          <ActionIcon
            key={emoji}
            variant="subtle"
            size="md"
            aria-label={t(`emoji.names.${name}`)}
            title={t(`emoji.names.${name}`)}
            disabled={disabled}
            onMouseDown={(event) => {
              // A pointer press keeps the focus, and so the cursor, in the text.
              event.preventDefault();
            }}
            onClick={() => {
              insert(emoji);
            }}
          >
            <span aria-hidden="true">{emoji}</span>
          </ActionIcon>
        ))}
      </Group>
      <Text size="xs" c="dimmed">
        {t('emoji.more')}
      </Text>
    </Group>
  );
}

export interface TextareaWithEmojiProps extends Omit<TextareaProps, 'value' | 'onChange'> {
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/** Mantine's `Textarea` with the quick row under it: the reply boxes on Comments and Messages. */
export function TextareaWithEmoji({ value, onChange, ...textarea }: TextareaWithEmojiProps) {
  const field = useRef<HTMLTextAreaElement>(null);
  return (
    <Stack gap={6}>
      <Textarea
        {...textarea}
        ref={field}
        value={value}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      />
      <QuickEmoji field={field} value={value} onChange={onChange} disabled={textarea.disabled} />
    </Stack>
  );
}

const graphemes =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : undefined;

/** How many characters a reader sees: one emoji is one. */
export function countGraphemes(text: string): number {
  if (graphemes === undefined) {
    // Code points, which is closer to a person's count than UTF-16 units are.
    return Array.from(text).length;
  }
  return Array.from(graphemes.segment(text)).length;
}
